import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { getBlogPosts } from '../services/api';
import { formatDate, excerptFor, coverFallback } from '../components/blog/blogUtils';

const styles = `
.bl-wrap { position: relative; background: #020617; min-height: 100vh; overflow: hidden; }
.bl-bg { position: absolute; inset: 0; z-index: 0; pointer-events: none; }
.bl-grid-tex { position: absolute; inset: 0; background-image: radial-gradient(circle, rgba(30,41,59,0.3) 1px, transparent 1px); background-size: 24px 24px; }
.bl-glow1 { position: absolute; inset: 0; background: radial-gradient(600px circle at 20% 30%, rgba(59,130,246,0.13), transparent 80%); }
.bl-glow2 { position: absolute; inset: 0; background: radial-gradient(400px circle at 85% 10%, rgba(139,92,246,0.10), transparent 80%); }
.bl-container { position: relative; z-index: 1; max-width: min(100rem, calc(100% - clamp(1.5rem, 5vw, 6rem))); margin: 0 auto; padding: 3.5rem clamp(1.25rem, 5vw, 4rem) 5rem; }
.bl-head { margin-bottom: 3rem; }
.bl-eyebrow { font-size: 0.72rem; font-weight: 800; letter-spacing: 0.16em; text-transform: uppercase; color: #60a5fa; }
.bl-title { font-size: clamp(2.2rem, 5vw, 3.4rem); font-weight: 800; letter-spacing: -0.03em; color: #f1f5f9; margin: 0.75rem 0 0.75rem; line-height: 1.08; }
.bl-title span { background: linear-gradient(90deg, #60a5fa, #818cf8); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; }
.bl-sub { font-size: 1.05rem; color: #94a3b8; max-width: 34rem; line-height: 1.7; margin: 0; }
.bl-layout { display: grid; grid-template-columns: 1fr; gap: 3rem; }
@media (min-width: 1024px) { .bl-layout { grid-template-columns: 2fr 1fr; gap: 3.5rem; } }
.bl-tag { display: inline-block; font-size: 0.62rem; font-weight: 700; letter-spacing: 0.09em; text-transform: uppercase; color: #60a5fa; background: rgba(59,130,246,0.1); border: 1px solid rgba(59,130,246,0.2); padding: 0.25rem 0.7rem; border-radius: 9999px; }
.bl-featured { background: #0a0f1e; border: 1px solid #1e293b; border-radius: 0.9rem; overflow: hidden; margin-bottom: 3rem; transition: border-color 0.3s, transform 0.3s; }
.bl-featured:hover { border-color: #334155; }
.bl-featured .cover { aspect-ratio: 16 / 9; overflow: hidden; }
.bl-featured .cover img, .bl-thumb img, .bl-feed-img img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.6s ease; }
.bl-featured:hover .cover img { transform: scale(1.04); }
.bl-featured .body { padding: clamp(1.5rem, 3vw, 2.5rem); }
.bl-featured h2 { font-size: clamp(1.6rem, 3vw, 2.3rem); font-weight: 800; color: #f1f5f9; line-height: 1.15; letter-spacing: -0.02em; margin: 1rem 0 1rem; transition: color 0.2s; }
.bl-featured a.title-link:hover h2 { color: #60a5fa; }
.bl-excerpt { color: #94a3b8; line-height: 1.75; }
.bl-clamp2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.bl-clamp3 { display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
.bl-avatar { width: 2rem; height: 2rem; border-radius: 50%; background: rgba(59,130,246,0.2); color: #60a5fa; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 0.72rem; flex-shrink: 0; }
.bl-read { color: #60a5fa; font-weight: 700; display: inline-flex; align-items: center; gap: 0.4rem; font-size: 0.9rem; }
.bl-read svg { transition: transform 0.2s; }
.bl-read:hover svg { transform: translateX(4px); }
.bl-feed { display: flex; flex-direction: column; gap: 2.5rem; }
.bl-feed-item { display: flex; flex-direction: column; gap: 1.25rem; border-bottom: 1px solid rgba(66,71,84,0.12); padding-bottom: 2.5rem; }
@media (min-width: 640px) { .bl-feed-item { flex-direction: row; gap: 1.75rem; } }
.bl-feed-img { width: 100%; aspect-ratio: 4 / 3; border-radius: 0.6rem; overflow: hidden; flex-shrink: 0; }
@media (min-width: 640px) { .bl-feed-img { width: 33%; } }
.bl-feed-item:hover .bl-feed-img img { transform: scale(1.05); }
.bl-feed-body { display: flex; flex-direction: column; justify-content: center; }
.bl-feed-cat { color: #60a5fa; font-weight: 700; font-size: 0.62rem; letter-spacing: 0.12em; text-transform: uppercase; margin-bottom: 0.5rem; }
.bl-feed-body h3 { font-size: 1.4rem; font-weight: 700; color: #f1f5f9; margin: 0 0 0.6rem; line-height: 1.3; transition: color 0.2s; }
.bl-feed-item:hover h3 { color: #60a5fa; }
.bl-meta { display: flex; align-items: center; gap: 1rem; font-size: 0.78rem; color: #64748b; }
.bl-sidebar-inner { position: sticky; top: 6rem; display: flex; flex-direction: column; gap: 2.5rem; }
.bl-side-h { color: #f1f5f9; font-weight: 700; font-size: 0.8rem; letter-spacing: 0.12em; text-transform: uppercase; border-bottom: 1px solid rgba(66,71,84,0.3); padding-bottom: 1rem; margin: 0 0 1.5rem; display: flex; align-items: center; justify-content: space-between; }
.bl-side-h .rule { width: 2rem; height: 2px; background: #3b82f6; }
.bl-recent { display: flex; flex-direction: column; gap: 1.5rem; }
.bl-recent-item { display: flex; gap: 1rem; text-decoration: none; }
.bl-thumb { width: 5rem; height: 5rem; border-radius: 0.4rem; overflow: hidden; flex-shrink: 0; }
.bl-recent-item h5 { color: #e1e2ec; font-weight: 700; font-size: 0.85rem; line-height: 1.4; margin: 0 0 0.3rem; transition: color 0.2s; }
.bl-recent-item:hover h5 { color: #60a5fa; }
.bl-recent-date { font-size: 0.68rem; color: #64748b; text-transform: uppercase; letter-spacing: 0.06em; }
.bl-news { background: #0a0f1e; border: 1px solid #1e293b; padding: 2rem; border-radius: 0.9rem; text-align: center; }
.bl-news h4 { color: #f1f5f9; font-size: 1.15rem; font-weight: 700; margin: 0 0 0.75rem; }
.bl-news p { color: #94a3b8; font-size: 0.85rem; margin: 0 0 1.25rem; line-height: 1.6; }
.bl-news input { width: 100%; background: #10131a; border: 1px solid #1e293b; border-radius: 0.5rem; padding: 0.7rem 1rem; color: #e2e8f0; font-size: 0.9rem; margin-bottom: 0.9rem; outline: none; box-sizing: border-box; }
.bl-news input:focus { border-color: #3b82f6; }
.bl-btn { background: linear-gradient(to bottom, #1d4ed8, #3b82f6); color: #fff; border: none; width: 100%; padding: 0.75rem; border-radius: 0.5rem; font-weight: 700; font-size: 0.9rem; cursor: pointer; transition: opacity 0.2s, box-shadow 0.2s; font-family: inherit; }
.bl-btn:hover { opacity: 0.9; box-shadow: 0 0 15px rgba(59,130,246,0.4); }
.bl-cats { display: flex; flex-wrap: wrap; gap: 0.5rem; }
.bl-cat { padding: 0.4rem 1rem; border-radius: 9999px; border: 1px solid #1e293b; color: #94a3b8; font-size: 0.78rem; cursor: pointer; background: transparent; transition: all 0.2s; font-family: inherit; }
.bl-cat:hover { border-color: #3b82f6; color: #60a5fa; }
.bl-cat.on { border-color: #3b82f6; color: #60a5fa; background: rgba(59,130,246,0.1); }
.bl-empty { text-align: center; padding: 4rem 1.5rem; border: 1px dashed rgba(51,65,85,0.5); border-radius: 1rem; color: #64748b; }
.bl-skel { background: linear-gradient(90deg, #0a0f1e, #131b2e, #0a0f1e); background-size: 200% 100%; animation: bl-pulse 1.4s ease infinite; border-radius: 0.6rem; }
@keyframes bl-pulse { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
`;

const initials = (name = '') => name.split(' ').map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || 'C';

function Cover({ post, className }) {
  if (post.coverImageUrl) {
    return <div className={className}><img src={post.coverImageUrl} alt={post.coverImageAlt || post.title} /></div>;
  }
  return <div className={className}><div style={{ width: '100%', height: '100%', background: coverFallback(post.slug || post.title) }} /></div>;
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    getBlogPosts().then((data) => { setPosts(data); setLoading(false); });
  }, []);

  const categories = Array.from(new Set(posts.map((p) => p.category).filter(Boolean)));
  const visible = activeCat ? posts.filter((p) => p.category === activeCat) : posts;
  const featured = visible[0];
  const feed = visible.slice(1);
  const recent = posts.slice(0, 3);

  return (
    <div className="bl-wrap">
      <style>{styles}</style>
      <div className="bl-bg" aria-hidden="true">
        <div className="bl-grid-tex" /><div className="bl-glow1" /><div className="bl-glow2" />
      </div>

      <div className="bl-container">
        <header className="bl-head">
          <span className="bl-eyebrow">Insights</span>
          <h1 className="bl-title">The Codinity <span>Blog</span></h1>
          <p className="bl-sub">Field notes from our engineers and designers, how we build fast apps, scalable platforms, and products that last.</p>
        </header>

        {loading ? (
          <div className="bl-layout">
            <div>
              <div className="bl-skel" style={{ height: '380px', marginBottom: '3rem' }} />
              <div className="bl-skel" style={{ height: '160px', marginBottom: '2rem' }} />
              <div className="bl-skel" style={{ height: '160px' }} />
            </div>
            <div className="bl-skel" style={{ height: '300px' }} />
          </div>
        ) : posts.length === 0 ? (
          <div className="bl-empty">
            <p style={{ fontSize: '1rem', color: '#94a3b8', marginBottom: '0.5rem' }}>No articles published yet</p>
            <p style={{ fontSize: '0.85rem' }}>Check back soon, we're writing.</p>
          </div>
        ) : (
          <div className="bl-layout">
            {/* Main column */}
            <div>
              {featured && (
                <article className="bl-featured">
                  <Link to={`/blog/${featured.slug}`} className="title-link" style={{ textDecoration: 'none' }}>
                    <Cover post={featured} className="cover" />
                  </Link>
                  <div className="body">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
                      {featured.category && <span className="bl-tag">{featured.category}</span>}
                      <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>{formatDate(featured.publishedAt || featured.createdAt)}</span>
                    </div>
                    <Link to={`/blog/${featured.slug}`} className="title-link" style={{ textDecoration: 'none' }}>
                      <h2>{featured.title}</h2>
                    </Link>
                    <p className="bl-excerpt bl-clamp3" style={{ marginBottom: '1.75rem' }}>{excerptFor(featured)}</p>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
                        <div className="bl-avatar">{initials(featured.author)}</div>
                        <span style={{ fontSize: '0.85rem', color: '#e1e2ec' }}>{featured.author || 'Codinity'}</span>
                      </div>
                      <Link to={`/blog/${featured.slug}`} className="bl-read">Read full article <ArrowRight size={15} /></Link>
                    </div>
                  </div>
                </article>
              )}

              {feed.length > 0 && (
                <div className="bl-feed">
                  {feed.map((post) => (
                    <article className="bl-feed-item" key={post._id}>
                      <Link to={`/blog/${post.slug}`} className="bl-feed-img"><Cover post={post} className="" /></Link>
                      <div className="bl-feed-body">
                        {post.category && <span className="bl-feed-cat">{post.category}</span>}
                        <Link to={`/blog/${post.slug}`} style={{ textDecoration: 'none' }}><h3>{post.title}</h3></Link>
                        <p className="bl-excerpt bl-clamp2" style={{ fontSize: '0.9rem', marginBottom: '0.9rem' }}>{excerptFor(post)}</p>
                        <div className="bl-meta">
                          <span>{formatDate(post.publishedAt || post.createdAt)}</span>
                          {post.readingMinutes && <span>{post.readingMinutes} min read</span>}
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}

              {visible.length === 0 && (
                <div className="bl-empty"><p>No articles in “{activeCat}” yet.</p></div>
              )}
            </div>

            {/* Sidebar */}
            <aside>
              <div className="bl-sidebar-inner">
                {recent.length > 0 && (
                  <div>
                    <h4 className="bl-side-h">Recent Posts <span className="rule" /></h4>
                    <div className="bl-recent">
                      {recent.map((post) => (
                        <Link to={`/blog/${post.slug}`} className="bl-recent-item" key={`r-${post._id}`}>
                          <Cover post={post} className="bl-thumb" />
                          <div>
                            <h5 className="bl-clamp2">{post.title}</h5>
                            <p className="bl-recent-date">{formatDate(post.publishedAt || post.createdAt)}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                <div className="bl-news">
                  <h4>Never Miss an Insight</h4>
                  <p>Get our occasional engineering digest in your inbox.</p>
                  {subscribed ? (
                    <p style={{ color: '#4ade80', margin: 0 }}>Thanks, you're on the list!</p>
                  ) : (
                    <form onSubmit={(e) => { e.preventDefault(); setSubscribed(true); }}>
                      <input type="email" required placeholder="Email address" />
                      <button type="submit" className="bl-btn">Subscribe Now</button>
                    </form>
                  )}
                </div>

                {categories.length > 0 && (
                  <div>
                    <h4 className="bl-side-h" style={{ display: 'block' }}>Categories</h4>
                    <div className="bl-cats">
                      <button className={`bl-cat${activeCat === '' ? ' on' : ''}`} onClick={() => setActiveCat('')}>All</button>
                      {categories.map((cat) => (
                        <button key={cat} className={`bl-cat${activeCat === cat ? ' on' : ''}`} onClick={() => setActiveCat(cat)}>{cat}</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}
